/**
 * 联机真源 → 牌桌渲染模型的单向镜像。
 *
 * 为什么需要它：`transport/applyMessage.ts` 的 `mirrorToTableStore` 刻意**不写**
 * `tableStore.view`（那是引擎的 `PublicHandState`，与 `PlayerView` 不同构）。
 * 但整套已完成的牌桌 UI —— `TableScene` / `useTableFx` / `tableDerive` —— 读的正是
 * `tableStore.view`。所以必须有人把 `buildTableVM(PlayerView)` 的结果送进去。
 *
 * 这不是「第二份真相」：写进去的东西是 `useNetStore.view` 的**纯函数**，
 * 单向、无反向写回，网络状态一变就整体重算。它等价于 §11.3 说的
 * 「渲染层 useMemo 派生」，只是派生结果必须落到 store 里才能被 TableScene 看见
 *（TableScene 从 store 读而不是收 props，那是它既定的接口，不该为联机模式改）。
 *
 * ★ 只在 `view` / `prompt` 的**对象身份**变化时才写：牌局高峰期每秒几十帧，
 *   而聊天、心跳这类帧根本不动 view。无条件写会让整张牌桌每帧重渲染，
 *   还会把 `tableStore.pending`（乐观 UI 的按钮态）反复清空。
 */
import type { ActionPrompt, PlayerView } from '@poker/shared'

import { useTableStore } from '../store/tableStore.js'
import { useNetStore, type NetState } from '../transport/applyMessage.js'

import { buildTableVM } from './buildTableVM.js'

interface MirrorBaseline {
  view: PlayerView | null
  prompt: ActionPrompt | null
  isActor: boolean
  /** 已经从牌桌上收走的那一手（见 `hideSettledHand`） */
  hiddenHandId: string | null
  /** 是否写过一次。首帧必须写，哪怕内容是 null（要把上一局的残留清掉） */
  primed: boolean
}

const baseline: MirrorBaseline = { view: null, prompt: null, isActor: false, hiddenHandId: null, primed: false }

/** 这一手是不是已经打完、而且房间层要求把它从牌桌上收走 */
function settledHandHidden(view: PlayerView | null, hiddenHandId: string | null): boolean {
  const hand = view?.hand ?? null
  return hand !== null && hand.phase === 'complete' && hand.handId === hiddenHandId
}

/** 把一份网络状态折成渲染模型写进 `tableStore`。幂等：内容没变就什么都不做。 */
export function mirrorVmToTableStore(state: NetState): void {
  if (
    baseline.primed &&
    state.view === baseline.view &&
    state.prompt === baseline.prompt &&
    state.isActor === baseline.isActor
  ) {
    return
  }
  baseline.view = state.view
  baseline.prompt = state.prompt
  baseline.isActor = state.isActor
  baseline.primed = true

  const heroSeat = state.view?.viewer.ownedSeat ?? null
  // 收走的那一手按「还没发牌」画：空桌 + 座位（座位上的筹码、角标来自另一条壳层镜像，不受影响）
  const vm = settledHandHidden(state.view, baseline.hiddenHandId) ? null : buildTableVM(state.view, heroSeat)
  // prompt 就是 LegalActions 的超集（ActionPrompt extends LegalActions），
  // 直接当 legal 用，不做任何字段裁剪 —— 裁剪就等于给「能否加注」开第二个判据（红线 #21）。
  //
  // 但 shadow 连接（同账号的另一台设备）也会收到 prompt，它**不能**拿到 legal：
  // 两台手机同时能点，必然有一台的点击被服务端拒掉。行动权的判据是 `isActor`。
  useTableStore.getState().applyHandView(vm, state.isActor ? state.prompt : null)
}

/**
 * 把一手已经打完的牌从牌桌上收走（传 null 撤销）。
 *
 * ★ 牌局暂停、等待室露出来以后，牌桌上还摆着上一手：公共牌、底池、庄家钮、
 *   「已弃牌 / ALL IN」、赢家光圈，操作区写着「本手结束」—— 可这一手早就结算完了，
 *   等待室在说的是下一件事（等人、等买分）。两层信息叠在一起，读起来像牌局卡在了半路。
 *   单手结算卡开着时**不收**：那一刻牌面要和结果一起看（由房间层决定什么时候调）。
 *
 * 只认这一手的 handId：下一手一发下来 id 就对不上，牌桌自动恢复，发牌动画照常播
 *（useTableFx 看到的是「从空桌到新的一手」，与开局第一手同一条路）。
 */
export function hideSettledHand(handId: string | null): void {
  if (baseline.hiddenHandId === handId) return
  baseline.hiddenHandId = handId
  // 网络状态没变，但牌桌该画的东西变了：强制重写一次
  baseline.primed = false
  mirrorVmToTableStore(useNetStore.getState())
}

/**
 * 订阅 net → table 的渲染模型镜像。返回取消订阅函数。
 *
 * 与 `startTableMirror()`（座位 / 房间码 / 连接相位）是两条独立的镜像，
 * 串联层两个都要起：那一条管牌桌的「壳」，这一条管牌桌的「内容」。
 */
export function startRoomVmMirror(): () => void {
  // 换房间/换会话时基线必须清掉，否则新房间的第一份视图会被当成「没变」而不下发
  baseline.view = null
  baseline.prompt = null
  baseline.isActor = false
  // hiddenHandId 刻意不清：房间屏在同一次挂载里先（layout effect）告诉这里收走哪一手，
  // 这条订阅后（普通 effect）才起 —— 在这里清掉，刷新进一个暂停中的房间就会把上一手摆回来。
  // 它只认那一手的 handId，换房间 / 下一手都对不上，留着无害；房间屏卸载时自己撤销
  baseline.primed = false
  mirrorVmToTableStore(useNetStore.getState())
  return useNetStore.subscribe((s) => {
    mirrorVmToTableStore(s)
  })
}
